import {makeTrackEvent} from './makeTrackEvent';

/**
 * Weave (query) panels (`src/weave/components/Panel2`) track their own interaction
 * events by calling these directly. Weave can't identify the panel it's rendered
 * in — that's app state — so the host supplies a link through weave's
 * PanelContext, and `toWeaveQueryPanelEventData` folds it in with the action.
 */

export type WeaveQueryPanelEventPage = 'workspace' | 'report' | 'weave';

export type WeaveQueryPanelEvent = {
  action: string;
  panelLink: string;
  page?: WeaveQueryPanelEventPage;
  panelPath?: string;
  panelType?: string;
  exprString?: string;
  newType?: string;
  oldType?: string;
  rowCount?: string;
  columnCount?: string;
  expSpine?: string;
  extra?: string;
  // Table shape, sent with Table.VIEW
  isGrouped?: boolean;
  isSorted?: boolean;
  hasFilter?: boolean;
  isJoined?: boolean;
  isConcatenated?: boolean;
  hasDerivedColumns?: boolean;
};

/**
 * Payload keys weave panels send today. Anything else is JSON-stringified into
 * `extra`
 */
const KNOWN_PAYLOAD_KEYS = [
  'panelPath',
  'panelType',
  'exprString',
  'newType',
  'oldType',
  'rowCount',
  'columnCount',
  'expSpine',
] as const;

/** Sent as real booleans rather than stringified, so they stay filterable. */
const BOOLEAN_PAYLOAD_KEYS = [
  'isGrouped',
  'isSorted',
  'hasFilter',
  'isJoined',
  'isConcatenated',
  'hasDerivedColumns',
] as const;

// Reasonable cap so we don't send unreasonable long strings to segment
const MAX_VALUE_LENGTH = 1024;

const truncate = (value: string) =>
  value.length > MAX_VALUE_LENGTH ? value.slice(0, MAX_VALUE_LENGTH) : value;

export function toWeaveQueryPanelEventData({
  action,
  panelLink,
  page,
  payload,
}: {
  action: string;
  panelLink: string;
  page?: WeaveQueryPanelEventPage;
  payload?: Record<string, unknown>;
}): WeaveQueryPanelEvent {
  const event: WeaveQueryPanelEvent = {action, panelLink: truncate(panelLink)};
  if (page != null) {
    event.page = page;
  }

  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload ?? {})) {
    if (value == null) {
      continue;
    }
    if ((BOOLEAN_PAYLOAD_KEYS as readonly string[]).includes(key)) {
      event[key as (typeof BOOLEAN_PAYLOAD_KEYS)[number]] = Boolean(value);
    } else if ((KNOWN_PAYLOAD_KEYS as readonly string[]).includes(key)) {
      event[key as (typeof KNOWN_PAYLOAD_KEYS)[number]] = truncate(
        String(value)
      );
    } else {
      extra[key] = value;
    }
  }

  if (Object.keys(extra).length > 0) {
    event.extra = truncate(JSON.stringify(extra));
  }

  return event;
}

export const trackWeaveQueryPanelEvent = makeTrackEvent<
  WeaveQueryPanelEvent,
  {
    action: {
      description: 'the panel and interaction that occurred, as <panel>.<ACTION>';
      exampleValues: [
        'Table.SELECT_COLUMNS',
        'Table.EXPORT_CSV',
        'Expression.SET_EXP',
      ];
    };
    panelLink: {
      description: 'absolute link to the panel the interaction happened in. for a workspace panel this is its full screen link, otherwise the page URL the panel was rendered on';
    };
    page: {
      description: 'what kind of page the panel was rendered on';
      exampleValues: ['workspace', 'report', 'weave'];
    };
    panelPath: {
      description: 'dot-separated path of the weave subpanel within the panel, since one panel can nest several';
      exampleValues: ['main', 'main.table'];
    };
    panelType: {
      description: 'the weave panel id involved in the action: what a table row is rendered with for TABLE_RENDER_AS, or what handles the expression for SPINE';
      exampleValues: ['table', 'plot', 'object'];
    };
    exprString: {
      description: 'the weave expression involved in the action, as a string';
    };
    newType: {
      description: 'for UPDATE_COLUMN_PANEL, the weave panel id the column changed to';
    };
    oldType: {
      description: 'for UPDATE_COLUMN_PANEL, the weave panel id the column changed from';
    };
    rowCount: {
      description: 'for EXPORT_CSV, how many rows were exported';
    };
    columnCount: {
      description: 'for EXPORT_CSV, how many columns were exported';
    };
    expSpine: {
      description: 'for SPINE, the chain of op names in the expression, without arguments. lower cardinality than exprString, so it groups across users';
      exampleValues: ['run-summary.pick', 'project-runs.limit.concat'];
    };
    isGrouped: {
      description: 'for VIEW, whether the table is grouped by at least one column';
    };
    isSorted: {
      description: 'for VIEW, whether the table is sorted by at least one column';
    };
    hasFilter: {
      description: 'for VIEW, whether a filter expression is applied to the rows';
    };
    isJoined: {
      description: 'for VIEW, whether the rows come from a joinAll expression, the only join a table logged today can use';
    };
    isConcatenated: {
      description: 'for VIEW, whether the rows come from a concat expression, i.e. tables stacked rather than joined';
    };
    hasDerivedColumns: {
      description: 'for VIEW, whether any column computes its value instead of reading a field, e.g. row["a"] / row["b"] rather than row["a"]';
    };
    extra: {
      description: 'JSON-encoded payload keys that are only known at runtime, currently just the config diff sent with UPDATE_CONFIG';
    };
    _description: 'Tracks interactions inside weave panels (tables, expressions, plots)';
    _location: 'workspaces, reports, weave pages';
    _motivation: 'track weave panel usage, and identify which panel an interaction came from';
  }
>('Weave Panel Event');
